import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { IRootThunkDispatch } from "../../redux/store";
import { OpenLoadingBarThunk } from "../../redux/common/isLoading/thunk";
import { OpenSnackBarWithResultFail, OpenSnackBarWithResultSuccess } from "../../redux/common/snackBar/thunk";
import { Button, Grid } from "@mui/material";
import { EditNote } from "@mui/icons-material";
import PdfDownloadDialog from "./PdfDownloadDialog";
import axios from "axios";
import ExcelJS from "exceljs";
import moment from "moment";
import {  insertPeopleCounterDataToExcel } from "../../util/cleaningExcel";
import { generateDailyCleaningDocx } from "../../util/docxGenerator";
import { ButtonTextGrid } from "../..";

interface CleaningReportDownloadProps {
    rowData?: any;
}

export default function CleaningReportDownload({ rowData }: CleaningReportDownloadProps) {
    const dispatch = useDispatch<IRootThunkDispatch>();
    const [openPdfDownloadDialog, setOpenPdfDownloadDialog] = useState<boolean>(false);
    const getDailyCleaningTemplateFileName = (imageTypeCount: number) => {
        const normalizedCount = Math.max(1, Math.min(5, imageTypeCount || 1));
        return `Generic daily cleaning template ${normalizedCount} col.docx`;
    };

    function computeImageTypeCount(zoneInfo: any): number {
        // Merge by English name: multiple type IDs with the same nameEn count as 1
        const nameEns = new Set<string>();
        // Add configured types (deduplicated by English name)
        for (const info of (zoneInfo.imageTypeInfo || [])) {
            nameEns.add(info.image_type_name_en);
        }
        // Add types found in actual data
        const allTypeInfo = zoneInfo.allImageTypeInfo || [];
        if (Array.isArray(zoneInfo.tableRows)) {
            for (const row of zoneInfo.tableRows) {
                if (row.imageGroups && typeof row.imageGroups === 'object') {
                    for (const typeId of Object.keys(row.imageGroups)) {
                        const photos = row.imageGroups[typeId];
                        if (Array.isArray(photos) && photos.length > 0) {
                            const match = allTypeInfo.find((t: any) => String(t.cleaning_image_type_id) === typeId);
                            if (match) {
                                nameEns.add(match.image_type_name_en);
                            } else {
                                nameEns.add(`type_${typeId}`);
                            }
                        }
                    }
                }
            }
        }
        return Math.min(5, Math.max(1, nameEns.size || 1));
    }

    async function resolveTemplatePath(imageTypeCount: number) {
        const templateCandidates = [
            getDailyCleaningTemplateFileName(imageTypeCount),
            "Generic daily cleaning template.docx",
            "Plastic daily cleaning template.docx",
        ];

        for (const fileName of templateCandidates) {
            try {
                const response = await fetch(`/templates/${fileName}`, { method: 'HEAD' });
                if (response.ok) {
                    return `/templates/${fileName}`;
                }
            } catch (error) {
                // try next candidate
            }
        }

        throw new Error("找不到可用的 Daily Cleaning 模板，請先建立 1~5 欄模板");
    };

    function handleConvertToPdf() {
        setOpenPdfDownloadDialog(true);
    }

    function filterOutPeopleCounter(zoneInfo: any): any {
        if (!zoneInfo) return zoneInfo;

        const PEOPLE_COUNTER_NAME_EN = 'people_counter';

        const filteredImageTypeInfo = (zoneInfo.imageTypeInfo || []).filter(
            (info: any) => info.image_type_name_en !== PEOPLE_COUNTER_NAME_EN
        );
        const filteredAllImageTypeInfo = (zoneInfo.allImageTypeInfo || []).filter(
            (info: any) => info.image_type_name_en !== PEOPLE_COUNTER_NAME_EN
        );

        const filteredTableRows = (zoneInfo.tableRows || []).map((row: any) => {
            if (!row.imageGroups || typeof row.imageGroups !== 'object') return row;
            const filteredGroups: Record<string, any> = {};
            for (const [typeId, photos] of Object.entries(row.imageGroups)) {
                const typeInfo = (zoneInfo.allImageTypeInfo || []).find(
                    (t: any) => String(t.cleaning_image_type_id) === typeId
                );
                if (typeInfo && typeInfo.image_type_name_en === PEOPLE_COUNTER_NAME_EN) continue;
                filteredGroups[typeId] = photos;
            }
            return { ...row, imageGroups: filteredGroups };
        });

        return {
            ...zoneInfo,
            imageTypeInfo: filteredImageTypeInfo,
            allImageTypeInfo: filteredAllImageTypeInfo,
            tableRows: filteredTableRows,
        };
    }

    function handleClosePdfDownloadDialog() {
        setOpenPdfDownloadDialog(false);
    }

    async function handlePdfDownloadSubmit(
        reportType: string,
        selectedDate?: moment.Moment | null,
        contractId?: number | null,
        zoneIds?: number[] | null,
        isInternalReport?: boolean,
        contractName?: string | null,
        _endDate?: moment.Moment | null,
        includePeopleCounter?: boolean
    ) {
        setOpenPdfDownloadDialog(false);
        dispatch(OpenLoadingBarThunk(true));
        try {
            const token = localStorage.getItem('loginToken');
            
            if (reportType === "DailyCleaning") {
                if (!selectedDate || !contractId) {
                    dispatch(OpenLoadingBarThunk(false));
                    dispatch(OpenSnackBarWithResultFail("請選擇日期和合約"));
                    return;
                }

                const docxFiles: File[] = [];

                if (!zoneIds || zoneIds.length === 0) {
                    const response = await axios.get(
                        `${process.env.REACT_APP_API}api/download/cleaningData`,
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                ...(token && { Authorization: `Bearer ${token}` })
                            },
                            params: {
                                date: selectedDate.format('YYYY-MM-DD'),
                                contract: contractId
                            }
                        }
                    );

                    if (!response.data || !response.data.data) {
                        dispatch(OpenLoadingBarThunk(false));
                        dispatch(OpenSnackBarWithResultFail("未能取得數據"));
                        return;
                    }

                    const zoneData = response.data.data;
                    const zoneNames = Object.keys(zoneData);

                    if (zoneNames.length === 0) {
                        dispatch(OpenLoadingBarThunk(false));
                        dispatch(OpenSnackBarWithResultFail("沒有找到清潔數據"));
                        return;
                    }

                    let sequenceCounter = 1;
                    for (const zoneName of zoneNames) {
                        let zoneInfo = zoneData[zoneName];
                        if (!includePeopleCounter) {
                            zoneInfo = filterOutPeopleCounter(zoneInfo);
                        }
                        const imageTypeCount = computeImageTypeCount(zoneInfo);
                        const templatePath = await resolveTemplatePath(imageTypeCount);
                        
                        const dateInUTC8 = selectedDate.clone().utcOffset(8);
                        const formattedDate = dateInUTC8.format('YYYY-MM-DD');
                        
                        const docxData = {
                            location: zoneInfo.location || zoneName || '',
                            date: formattedDate,
                            reportTitle: zoneInfo.reportTitle || '',
                            contactPerson: zoneInfo.contactPerson || '',
                            tel: zoneInfo.tel || '',
                            email: zoneInfo.email || '',
                            contractNumber: zoneInfo.contractNumber || '',
                            imageTypeInfo: zoneInfo.imageTypeInfo || [],
                            allImageTypeInfo: zoneInfo.allImageTypeInfo || [],
                            tableRows: zoneInfo.tableRows
                        };

                        const docxBlob = await generateDailyCleaningDocx(templatePath, docxData);
                        const contractReportSequence = zoneInfo.contractReportSequence;
                        const docxFileName = contractReportSequence 
                            ? `${contractReportSequence}.docx`
                            : `${sequenceCounter}.docx`;
                        if (!contractReportSequence) {
                            sequenceCounter++;
                        }
                        const docxFile = new File([docxBlob], docxFileName, {
                            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                        });
                        docxFiles.push(docxFile);
                    }
                } else {
                    let sequenceCounter = 1;
                    const processedSequences = new Set<string>();
                    for (const zoneId of zoneIds) {
                        const response = await axios.get(
                            `${process.env.REACT_APP_API}api/download/cleaningData`,
                            {
                                headers: {
                                    'Content-Type': 'application/json',
                                    ...(token && { Authorization: `Bearer ${token}` })
                                },
                                params: {
                                    date: selectedDate.format('YYYY-MM-DD'),
                                    contract: contractId,
                                    zoneId: zoneId
                                }
                            }
                        );

                        if (!response.data || !response.data.data) {
                            continue;
                        }

                        const zoneData = response.data.data;
                        const zoneNames = Object.keys(zoneData);

                        if (zoneNames.length === 0) {
                            continue;
                        }

                        for (const zoneName of zoneNames) {
                            let zoneInfo = zoneData[zoneName];
                            if (!includePeopleCounter) {
                                zoneInfo = filterOutPeopleCounter(zoneInfo);
                            }
                            const imageTypeCount = computeImageTypeCount(zoneInfo);
                            const templatePath = await resolveTemplatePath(imageTypeCount);
                            
                            const dateInUTC8 = selectedDate.clone().utcOffset(8);
                            const formattedDate = dateInUTC8.format('YYYY-MM-DD');
                            
                            const docxData = {
                                location: zoneInfo.location || zoneName || '',
                                date: formattedDate,
                                reportTitle: zoneInfo.reportTitle || '',
                                contactPerson: zoneInfo.contactPerson || '',
                                tel: zoneInfo.tel || '',
                                email: zoneInfo.email || '',
                                contractNumber: zoneInfo.contractNumber || '',
                                imageTypeInfo: zoneInfo.imageTypeInfo || [],
                                allImageTypeInfo: zoneInfo.allImageTypeInfo || [],
                                tableRows: zoneInfo.tableRows
                            };

                            const docxBlob = await generateDailyCleaningDocx(templatePath, docxData);
                            const contractReportSequence = zoneInfo.contractReportSequence;
                            let docxFileName = contractReportSequence 
                                ? `${contractReportSequence}.docx`
                                : `${sequenceCounter}.docx`;
                            
                            if (processedSequences.has(docxFileName)) {
                                docxFileName = contractReportSequence 
                                    ? `${contractReportSequence}_${zoneName}.docx`
                                    : `${sequenceCounter}_${zoneName}.docx`;
                            }
                            processedSequences.add(docxFileName);
                            if (!contractReportSequence) {
                                sequenceCounter++;
                            }
                            
                            const docxFile = new File([docxBlob], docxFileName, {
                                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                            });
                            docxFiles.push(docxFile);
                        }
                    }
                }

                if (docxFiles.length === 0) {
                    dispatch(OpenLoadingBarThunk(false));
                    dispatch(OpenSnackBarWithResultFail("沒有找到清潔數據"));
                    return;
                }

                if (docxFiles.length === 1) {
                    const docxBlob = docxFiles[0];
                    const url = URL.createObjectURL(docxBlob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = docxFiles[0].name;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);

                    dispatch(OpenLoadingBarThunk(false));
                    dispatch(OpenSnackBarWithResultSuccess("文件下載成功"));
                    setOpenPdfDownloadDialog(false);
                } else {
                    for (const docxFile of docxFiles) {
                        const url = URL.createObjectURL(docxFile);
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = docxFile.name;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(url);
                    }

                    dispatch(OpenLoadingBarThunk(false));
                    dispatch(OpenSnackBarWithResultSuccess("文件下載成功"));
                    setOpenPdfDownloadDialog(false);
                }
            } else {
                // inspection 
                if (!selectedDate) {
                    dispatch(OpenLoadingBarThunk(false));
                    dispatch(OpenSnackBarWithResultFail("請選擇月份"));
                    return;
                }
                
                const params: Record<string, string> = {};
                
                if (selectedDate) {
                    params.date = selectedDate.format('YYYY-MM');
                }
                
                const response = await axios.get(
                    `${process.env.REACT_APP_API}api/download/cleaningPeopleCounterByMonth`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            ...(token && { Authorization: `Bearer ${token}` })
                        },
                        params
                    }
                );
                
                if (!response.data || !response.data.data) {
                    dispatch(OpenLoadingBarThunk(false));
                    dispatch(OpenSnackBarWithResultFail("未能取得數據"));
                    return;
                }
                
                const peopleCounterData = response.data.data;
                
                const templateFileName = reportType === "129PeopleCounter" 
                    ? "129_25 - People Counter Report - Convert Template.xlsx"
                    : "139_25 - People Counter Report - Convert Template.xlsx";
                
                const templateResponse = await fetch(`/templates/${templateFileName}`);
                if (!templateResponse.ok) {
                    dispatch(OpenLoadingBarThunk(false));
                    dispatch(OpenSnackBarWithResultFail(`無法載入模板文件: ${templateFileName}`));
                    return;
                }
                
                const templateArrayBuffer = await templateResponse.arrayBuffer();
                const workbook = new ExcelJS.Workbook();
                await workbook.xlsx.load(templateArrayBuffer);
                
                await insertPeopleCounterDataToExcel(workbook, peopleCounterData, selectedDate);
                
                const buffer = await workbook.xlsx.writeBuffer();
                const blob = new Blob([buffer], { 
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
                });
                const fileName = `${reportType}_${selectedDate?.format('YYYYMM') || 'report'}.xlsx`;
                
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                
                dispatch(OpenLoadingBarThunk(false));
                dispatch(OpenSnackBarWithResultSuccess("文件下載成功"));
                setOpenPdfDownloadDialog(false);
            }
        } catch (error) {
            console.log('error',error)
            dispatch(OpenLoadingBarThunk(false));
            dispatch(OpenSnackBarWithResultFail("未能生成文件"));
        }
    }

    return (
        <>
            <Button
                variant="contained"
                onClick={handleConvertToPdf}
            >
                 <ButtonTextGrid>
               <EditNote />
                <Grid>下載</Grid>
                 </ButtonTextGrid>
 
            </Button>

            <PdfDownloadDialog
                open={openPdfDownloadDialog}
                onClose={handleClosePdfDownloadDialog}
                onSubmit={handlePdfDownloadSubmit}
                reportType="daily_cleaning"
                defaultDate={rowData?.date ? moment(rowData.date) : null}
            />
        </>
    );
}
